import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import React, { useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Loader } from '@/components/Loader';
import { Tabs, type TabOption } from '@/components/chariot-ui';
import { ClientBudgetTab } from '@/sections/customerManagement/components/masterDashboard/ClientBudgetTab';
import { ClientJobsTab } from '@/sections/customerManagement/components/masterDashboard/ClientJobsTab';
import { ClientRunTab } from '@/sections/customerManagement/components/masterDashboard/ClientRunTab';
import Avatar from '@/sections/topNavBar/Avatar';
import { useCustomerManagement } from '@/sections/customerManagement/hooks/useCustomerManagement';
import { type Status } from '@/sections/customerManagement/types';

const statusBorderColor: Record<Status, string> = {
  Active: 'border-l-chariot-stroke-info',
  Paused: 'border-l-chariot-stroke-high',
  Completed: 'border-l-chariot-stroke-critical',
  Setup: 'border-l-transparent',
  Upcoming: 'border-l-transparent',
};

const clientTabs: TabOption[] = [
  { label: 'Run', value: 'run' },
  { label: 'Budget / Limits', value: 'budget' },
  { label: 'Jobs', value: 'jobs' },
];

export const DiscoveryPanel: React.FC = () => {
  const { customers, customersStatus } = useCustomerManagement();
  const [selectedClient, setSelectedClient] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<TabOption>(clientTabs[0]);
  const [search, setSearch] = useState('');

  const filteredClients = useMemo(
    () =>
      customers.filter(
        c =>
          c.displayName.toLowerCase().includes(search.toLowerCase()) ||
          c.email.toLowerCase().includes(search.toLowerCase())
      ),
    [customers, search]
  );

  const selected = useMemo(
    () => customers.find(c => c.email === selectedClient),
    [customers, selectedClient]
  );

  if (customersStatus === 'pending') {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader />
      </div>
    );
  }

  if (customersStatus === 'error') {
    return (
      <p className="text-chariot-text-secondary py-8 text-center text-sm">
        Failed to load customer data. Try refreshing the page.
      </p>
    );
  }

  return (
    <div className="flex h-full gap-4">
      {/* Left: client list */}
      <div className="flex w-72 shrink-0 flex-col gap-2">
        <div className="border-chariot-stroke bg-chariot-background-primary flex items-center gap-2 rounded-lg border px-3 py-2">
          <MagnifyingGlassIcon className="text-chariot-text-tertiary size-4 shrink-0" />
          <input
            type="text"
            placeholder="Search clients..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="text-chariot-text-primary placeholder:text-chariot-text-tertiary w-full bg-transparent text-sm outline-none"
          />
          <span className="text-chariot-text-tertiary shrink-0 text-xs">
            {filteredClients.length}
          </span>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filteredClients.map(client => (
            <button
              key={client.email}
              type="button"
              onClick={() => setSelectedClient(client.email)}
              className={twMerge(
                'flex w-full items-center gap-3 rounded-lg border-l-4 px-3 py-2.5 text-left transition-colors',
                statusBorderColor[client.status] ?? 'border-l-transparent',
                selectedClient === client.email
                  ? 'bg-blue-500/10'
                  : 'hover:bg-chariot-background-secondary'
              )}
            >
              <div className="size-7 shrink-0">
                <Avatar
                  email={client.email}
                  className="size-7 rounded-full object-cover"
                  isOrganization={true}
                />
              </div>
              <div className="flex min-w-0 flex-col">
                <span className="text-chariot-text-primary truncate text-sm font-medium">
                  {client.displayName}
                </span>
                <span className="text-chariot-text-tertiary truncate text-xs">
                  {client.email}
                </span>
              </div>
            </button>
          ))}

          {filteredClients.length === 0 && (
            <p className="text-chariot-text-secondary py-8 text-center text-sm">
              No clients found.
            </p>
          )}
        </div>
      </div>

      {/* Right: client detail */}
      <div className="border-chariot-stroke flex min-w-0 flex-1 flex-col rounded-lg border">
        {selected ? (
          <>
            <div className="border-chariot-stroke flex items-center gap-3 border-b px-5 py-3">
              <div className="size-8 shrink-0">
                <Avatar
                  email={selected.email}
                  className="size-8 rounded-full object-cover"
                  isOrganization={true}
                />
              </div>
              <div className="flex min-w-0 flex-col">
                <span className="text-chariot-text-primary truncate text-sm font-medium">
                  {selected.displayName}
                </span>
                <span className="text-chariot-text-tertiary truncate text-xs">
                  {selected.email}
                </span>
              </div>
            </div>

            <div className="border-chariot-stroke border-b px-5 pt-3">
              <Tabs
                tabs={clientTabs}
                selected={selectedTab}
                onSelect={setSelectedTab}
                variant="primary"
              />
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {selectedTab.value === 'run' && (
                <ClientRunTab client={selected.email} />
              )}
              {selectedTab.value === 'budget' && (
                <ClientBudgetTab client={selected.email} />
              )}
              {selectedTab.value === 'jobs' && (
                <ClientJobsTab client={selected.email} />
              )}
            </div>
          </>
        ) : (
          <div className="flex flex-1 items-center justify-center">
            <p className="text-chariot-text-tertiary text-sm">
              Select a client to get started
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
